<template>
  <div :class="[size, { 'bitts-tag--slim': slim }]" class="bitts-tag">
    <tag :class="colorClassName">
      <slot name="pre" />
      <slot>{{ text }}</slot>
      <slot name="post" />
    </tag>
  </div>
</template>

<script setup lang="ts">
import { StrictExclude } from '@crossbeam/types';

import { Tag } from 'ant-design-vue';
import { computed } from 'vue';

import { BittsSize } from '../types';

const {
  color = 'neutral',
  size = 'small',
  slim = false,
  text = '',
} = defineProps<{
  color?: string;
  slim?: boolean;
  text?: string;
  size?: StrictExclude<BittsSize, 'tiny' | 'medium' | 'large' | 'x-large'>;
}>();

const MAP_COLOR_TO_CLASS: Record<string, string> = {
  accent: 'text-accent-text bg-accent-background-weak',
  beta: 'bg-linear-to-r from-beta-border to-beta-text text-white',
  copilot: 'bg-primary-background-weak text-primary-text',
  'copilot-gradient':
    'bg-linear-to-r from-blue-50 to-blue-50/20 text-primary-text',
  'copilot-neutral': 'bg-neutral-background-weak text-neutral-text',
  danger: 'bg-danger-background-weak text-danger-text',
  info: 'bg-info-background-weak text-info-text',
  neutral: 'bg-neutral-background text-neutral-text',
  new: 'bg-linear-to-r from-blue-700 to-primary-accent text-white',
  partner: 'bg-linear-to-r from-neutral-100 to-white text-neutral-text-weak',
  secondary: 'bg-secondary-background-weak text-secondary-text',
  'secondary-outline':
    'bg-white border-solid border border-secondary-border text-secondary-text',
  success: 'bg-success-background-weak text-success-text',
  upsell:
    'bg-linear-to-r text-upsell-text-strong from-upsell-background-weak to-upsell-background-weak/20',
  warning: 'bg-warning-background-weak text-warning-text',
  white: 'bg-white',
};

const colorClassName = computed<string>(
  () => MAP_COLOR_TO_CLASS[color] || color,
);
</script>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-tag {
  .ant-tag {
    @apply inline-flex items-center text-base border-0 leading-4 px-10 py-4 rounded-bts-lg mr-4;
    font-family: inherit;
  }

  .ant-tag-hidden {
    @apply block;
  }

  &.x-small .ant-tag {
    @apply text-sm py-2;
  }

  &.bitts-tag--slim .ant-tag {
    @apply py-0;
  }

  /* turn off animations for all tags we don't use this component for any click functionality */
  [ant-click-animating-without-extra-node='true']::after,
  .ant-click-animating-node {
    -webkit-animation: none;
    animation: none;
  }
}
</style>
